import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Document {
  id: string
  name: string
  type: string
  category: string
  file_type: string
  file_size_mb: number
  status: string
  version: number
  created_by: string
  created_at: string
  modified_at: string
  expiry_date: string | null
  tags: string[]
  access_level: string
}

interface Folder {
  id: string
  name: string
  document_count: number
  size_mb: number
  access_level: string
}

interface Activity {
  id: string
  action: string
  document_name: string
  user: string
  timestamp: string
}

export default function DocumentManagementPage() {
  const [documents, setDocuments] = useState<Document[]>([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [activities, setActivities] = useState<Activity[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [docsRes, foldersRes, actRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/documents/list'),
      fetch(API_BASE + '/documents/folders'),
      fetch(API_BASE + '/documents/activities'),
      fetch(API_BASE + '/documents/statistics')
    ])
    if (docsRes.ok) setDocuments((await docsRes.json()).documents || [])
    if (foldersRes.ok) setFolders((await foldersRes.json()).folders || [])
    if (actRes.ok) setActivities((await actRes.json()).activities || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'pdf': return '📄'
      case 'docx': return '📝'
      case 'pptx': return '📊'
      case 'xlsx': return '📈'
      default: return '📁'
    }
  }

  const getAccessColor = (level: string) => {
    switch (level) {
      case 'public': return 'bg-green-100 text-green-800'
      case 'internal': return 'bg-blue-100 text-blue-800'
      case 'restricted': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Document Management</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Upload Document</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Documents</div>
              <div className="text-2xl font-bold">{statistics.total_documents}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Folders</div>
              <div className="text-2xl font-bold">{statistics.total_folders}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Storage Used</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_storage_mb?.toFixed(1)} MB</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Expiring Soon</div>
              <div className="text-2xl font-bold text-orange-600">{statistics.expiring_soon}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Public Docs</div>
              <div className="text-2xl font-bold text-green-600">{statistics.public_documents}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4 mb-8">
          {folders.map(folder => (
            <div key={folder.id} className="bg-white rounded-lg shadow p-4 hover:shadow-md cursor-pointer">
              <div className="text-3xl mb-2">📁</div>
              <div className="font-semibold">{folder.name}</div>
              <div className="text-sm text-gray-500">{folder.document_count} files • {folder.size_mb.toFixed(1)} MB</div>
              <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs ${getAccessColor(folder.access_level)}`}>
                {folder.access_level}
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Documents</h2></div>
            <div className="divide-y">
              {documents.map(doc => (
                <div key={doc.id} className="p-4 flex items-center gap-4">
                  <div className="text-2xl">{getFileIcon(doc.file_type)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{doc.name}</span>
                      <span className="text-xs text-gray-400">v{doc.version}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {doc.category} • {doc.file_size_mb.toFixed(1)} MB • {doc.created_by}
                    </div>
                    <div className="flex gap-1 mt-1">
                      {doc.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded text-xs ${getAccessColor(doc.access_level)}`}>{doc.access_level}</span>
                    <div className="text-xs text-gray-500 mt-1">{new Date(doc.modified_at).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-4">
              {activities.map(activity => (
                <div key={activity.id} className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm ${
                    activity.action === 'upload' ? 'bg-green-500' :
                    activity.action === 'download' ? 'bg-blue-500' :
                    activity.action === 'edit' ? 'bg-orange-500' : 'bg-purple-500'
                  }`}>
                    {activity.action[0].toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm"><span className="font-medium">{activity.user}</span> {activity.action}ed</div>
                    <div className="text-sm text-gray-600">{activity.document_name}</div>
                    <div className="text-xs text-gray-400">{new Date(activity.timestamp).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
